import { InvalidPathError } from './errors';

type WithoutTrailingSlashes<T extends string> = T extends `${infer L}/`
  ? WithoutTrailingSlashes<L>
  : T;

export const removeTrailingSlashesFromPath = <T extends string>(
  path: T,
): WithoutTrailingSlashes<T> => {
  let runningPath = path as WithoutTrailingSlashes<T>;
  while (runningPath.endsWith('/')) {
    runningPath = runningPath.slice(0, -1) as WithoutTrailingSlashes<T>;
  }
  return runningPath;
};

type WithoutLeadingSlashes<T extends string> = T extends `/${infer R}`
  ? WithoutLeadingSlashes<R>
  : T;

export const removeLeadingSlashesFromPath = <T extends string>(
  path: T,
): WithoutLeadingSlashes<T> => {
  let runningPath = path as WithoutLeadingSlashes<T>;
  while (runningPath.startsWith('/')) {
    runningPath = runningPath.slice(1) as WithoutLeadingSlashes<T>;
  }
  return runningPath;
};

/**
 * A regular expression that is used to match valid path definitions.  A path is considered valid
 * if it is a collection of forward-slashes separated by alphanumeric, hyphen, or underscore
 * characters.
 */
const PathRegex = /^\/(?:(?:[a-zA-Z0-9-_]+\/)*[a-zA-Z0-9-_]+)?\/?$/;

export type Path = `/${string}`;

/**
 * A typeguard that returns whether or not the provided value is a path, based solely on the type
 * definition of {@link Path}.  This means that the only condition that is checked is whether or
 * not the provided value, {@link string}, begins with a forward-slash.
 *
 * This typeguard should not be used for validating that the form of the path is valid, outside of
 * whether or not the path begins with a forward slash.
 */
export const isPath = (path: string): path is Path => path.startsWith('/');

export type PathAssertion = (path: string) => asserts path is Path;

/**
 * A type assertion that asserts that the provided value, {@link string}, is consistent with the
 * {@link Path} type definition.  This assertion only checks that the provided value begins with a
 * forward-slash, and does not validate the form of the path beyond that.
 *
 * If it is necessary that the form of the path be validated in it's entirety, based on the
 * {@link PathRegex}, refer to the {@link assertIsValidPath} method instead.
 */
export const assertIsPath: PathAssertion = path => {
  if (!isPath(path)) {
    throw new InvalidPathError(path, "The path does not begin with a '/'.");
  }
};

/**
 * Asserts that the provided value, {@link string}, is consistent with the {@link Path} type
 * definition, returning the original value if this is the case.
 *
 * If the provided value is not consistent with the {@link Path} type definition, an error,
 * {@link InvalidPathError} will be thrown.
 *
 * @param {string} path
 *   The value that should be asserted to be a valid path, {@link Path}.
 *
 * @throws {InvalidPathError} Throws an error if the provided value is not a valid path.
 */
export const ensureIsPath = (path: string): Path => {
  assertIsPath(path);
  return path;
};

/**
 * A type assertion that asserts that the provided value, {@link string}, is consistent with the
 * {@link Path} type definition, **and** that the form of the provided value is a valid path, based
 * on the {@link PathRegex}.
 *
 * @param {string} path
 *   The value that should be asserted to be a valid path, {@link Path}, and consistent with the
 *   form of a valid path, as determined by the {@link PathRegex}.
 *
 * @throws {InvalidPathError}
 *   Throws an error, {@link InvalidPathError}, if the provided value, {@link string}, is not
 *   consistent with the type definition of a {@link Path}, or the form of the path is invalid,
 *   as deemed by the {@link PathRegex}.
 */
export const assertIsValidPath: PathAssertion = path => {
  if (!isPath(path)) {
    throw new InvalidPathError(path, "The path does not begin with a '/'.");
  }
  if (!PathRegex.test(path)) {
    throw new InvalidPathError(path, 'The path does not match the expected regex.');
  }
};
